import { useImperativeHandle, forwardRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Input, Select, Checkbox } from "antd";
import type { CheckboxValueType } from "antd/es/checkbox/Group";
import { FillInBlankInputs } from "..";
import { useSelector } from "react-redux";
import { fetchHistory } from "src/actions/historyTrial";
import { FOOTNOTE_GROUP } from "src/DigitalProtocolModule/constants";
import styles from "./FootnoteDetailsByTypes.module.scss";
import "./FootnoteDetailsByTypes.scss";

const { TextArea } = Input;

interface SoaDetailsState {
  trial_id: string;
  user_name: string;
  tableItems: Digital_Protocol_SoA.LandingInfoType;
  dprot_id: string;
  unit: string;
  winUnit: string;
}

interface CommonDetailProps {
  name?: string;
  jsonData: any;
  configOptions?: FootnoteConfig.Options[];
  footnoteGroupName?: string;
  ref?: any;
  activity?: Digital_Protocol_SoA_Table_Data.Entity;
  handleJsonDataChange: (
    field: string | string[],
    value: number | string | any[],
    previewTextPre: string,
    previewTextValue: string
  ) => void;
}

/*
  Component: Details for "Activity only applicable to certain patient population"
*/
export const PatientPopulation = forwardRef(
  ({ name, jsonData, handleJsonDataChange }: CommonDetailProps, ref: any) => {
    useImperativeHandle(ref, () => ({
      doValidation: () => doValidation(),
    }));

    const [errorMsg, setErrorMsg] = useState<string>("");

    const doValidation = () => {
      let isValid = true;
      if (!jsonData?.patient_population_subject_text?.trim()) {
        setErrorMsg("Please fill in the sentence.");
        isValid = false;
      } else {
        setErrorMsg("");
      }
      return isValid;
    };

    const label = `${name} is only performed for subjects that`;

    const fields = [
      [
        {
          type: "text",
          label,
        },
        {
          type: "input",
          inputProp: {
            value: jsonData.patient_population_subject_text,
            onChange: (evt) => {
              const previewPre = evt.target.value?.trim()
                ? `${label} ${evt.target.value?.trim()}`
                : "";
              handleJsonDataChange(
                "patient_population_subject_text",
                evt.target.value,
                previewPre,
                `${previewPre} ${jsonData?.add_free_text || ""}`
              );
            },
            style: { width: "60%" },
          },
        },
      ],
    ];

    return (
      <>
        {fields.map((group, i) => (
          <Input.Group className={styles.inputGroup} key={i}>
            {group.map((field, index) => (
              <FillInBlankInputs {...field} key={index} />
            ))}
          </Input.Group>
        ))}
        {errorMsg && <div className={styles.errorMsg}>{errorMsg}</div>}
      </>
    );
  }
);

/*
  Component: Details for "Activity has sub-activities"
*/
export const SubActivity = forwardRef(
  (
    {
      name,
      jsonData,
      footnoteGroupName,
      handleJsonDataChange,
    }: CommonDetailProps,
    ref: any
  ) => {
    // get footnote config
    const footnoteConfigState = useSelector(fetchHistory).data.dpFootnoteConfig;

    useImperativeHandle(ref, () => ({
      doValidation: () => doValidation(),
    }));

    const [errorMsg, setErrorMsg] = useState<string>("");

    const includesOptions = footnoteConfigState.activityOptionList.map(
      (item) => ({
        value: item.activity_id,
        label: item["Standard Activity"],
      })
    );

    const doValidation = () => {
      let isValid = true;
      if (!(jsonData?.sub_activity_includes_select?.length > 0)) {
        setErrorMsg("Includes is required.");
        isValid = false;
      } else {
        setErrorMsg("");
      }
      return isValid;
    };

    const label =
      footnoteGroupName === FOOTNOTE_GROUP.ACTIVITY
        ? `${name} includes:`
        : footnoteGroupName === FOOTNOTE_GROUP.VISIT ||
          footnoteGroupName === FOOTNOTE_GROUP.PERIOD
        ? `The following activities will be ongoing during ${name}:`
        : ``;

    const fields = [
      [
        {
          type: "label",
          label,
          required: true,
        },
        {
          type: "select",
          inputProp: {
            value: jsonData.sub_activity_includes_select,
            onChange: (val: number[]) => {
              const includesSelectedList = includesOptions.filter((item) =>
                val.includes(item.value)
              );
              const includesText = includesSelectedList
                .map((item, index) => {
                  if (
                    index !== 0 &&
                    index === includesSelectedList.length - 1
                  ) {
                    return `and ${item.label}.`;
                  } else {
                    return `${item.label}${
                      includesSelectedList.length === 1 ? "." : ""
                    }`;
                  }
                })
                .join(", ");
              const previewPre =
                val.length > 0 ? `${label} ${includesText}` : "";
              handleJsonDataChange(
                "sub_activity_includes_select",
                val,
                previewPre,
                `${previewPre} ${jsonData?.add_free_text || ""}`
              );
            },
            mode: "multiple",
            showArrow: true,
            allowClear: true,
            filterOption: (input, option: any) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase()),
            filterSort: (optionA: any, optionB: any) =>
              (optionA?.label ?? "")
                .toLowerCase()
                .localeCompare((optionB?.label ?? "").toLowerCase()),
            options: includesOptions,
            style: { width: "510px" },
          },
        },
      ],
    ];

    return (
      <>
        {fields.map((group, i) => (
          <Input.Group className={styles.inputGroup} key={i}>
            {group.map((field, index) => (
              <FillInBlankInputs {...field} key={index} />
            ))}
          </Input.Group>
        ))}
        {errorMsg && <div className={styles.errorMsg}>{errorMsg}</div>}
      </>
    );
  }
);

/*
  Component: Details for "Activity has a different workflow for certain patients"
*/
export const DifferentWorkflow = forwardRef(
  (
    { jsonData, configOptions, handleJsonDataChange }: CommonDetailProps,
    ref: any
  ) => {
    const IS_WORKFLOW_2_OPTION = 2;

    const dpSoaState = useSelector((state: any) => state?.dpSoaReducer);

    useImperativeHandle(ref, () => ({
      doValidation: () => doValidation(),
    }));

    const [workflowErrorMsg, setWorkflowErrorMsg] = useState<string>("");
    const [errorMsg, setErrorMsg] = useState<string>("");

    const doValidation = () => {
      let isValid = true;
      if (!jsonData.differentWorkflowType) {
        setWorkflowErrorMsg("New workflow is required.");
        setErrorMsg("");
        isValid = false;
      } else if (
        jsonData.differentWorkflowType === "workflow_1" &&
        (!jsonData?.different_workflow_1_text?.trim() ||
          !jsonData?.different_workflow_1_select)
      ) {
        setWorkflowErrorMsg("");
        setErrorMsg("Please fill in the sentence.");
        isValid = false;
      } else if (
        jsonData.differentWorkflowType === "workflow_1" &&
        jsonData?.different_workflow_1_select &&
        !workflow1Options
          .map((item) => item.value)
          .includes(jsonData?.different_workflow_1_select)
      ) {
        setWorkflowErrorMsg("");
        setErrorMsg(
          "The No. of visits should be limited to maximum No. of total visits in the SoA builder table."
        );
        isValid = false;
      } else if (
        jsonData.differentWorkflowType === "workflow_2" &&
        (!jsonData?.different_workflow_2_text?.trim() ||
          !jsonData?.different_workflow_2_select)
      ) {
        setWorkflowErrorMsg("");
        setErrorMsg("Please fill in the sentence.");
        isValid = false;
      } else {
        setWorkflowErrorMsg("");
        setErrorMsg("");
      }
      return isValid;
    };

    const workflowTypeOptions = [
      {
        value: "workflow_1",
        label: "Patient goes to a different visit",
      },
      {
        value: "workflow_2",
        label:
          "Patient has to complete a different set of activities within the visit",
      },
    ];

    const workflow1Options = (dpSoaState.soaVisits || []).map(
      (item: number) => ({
        value: item,
        label: item,
      })
    );

    const workflow2Options = configOptions
      .filter((item) => item.dropdown_id === IS_WORKFLOW_2_OPTION)
      .map((item) => ({
        value: item.fd_dropdown_id,
        label: item.dropdown_text,
      }));

    const label1_1 = `Patient with`;
    const label1_2 = `should processed to`;
    const label1_3 = `visit.`;
    const label2_1 = `If the patient`;
    const label2_2 = `should be administered.`;

    const generateWorkflow1Pre = (
      workflow_1_text: string,
      workflow_1_select: number
    ) => {
      const selectLabel = workflow_1_select
        ? workflow1Options.find((item) => item.value === workflow_1_select)
            ?.label || workflow_1_select
        : "";
      const preTxt = `${label1_1} ${
        workflow_1_text?.trim() || ""
      } ${label1_2} ${selectLabel} ${label1_3}`;
      return preTxt;
    };

    const generateWorkflow2Pre = (
      workflow_2_text: string,
      workflow_2_select: number
    ) => {
      const selectLabel = workflow_2_select
        ? workflow2Options.find((item) => item.value === workflow_2_select)
            .label
        : "";
      const preTxt = `${label2_1} ${
        workflow_2_text?.trim() || ""
      } ${selectLabel} ${label2_2}`;
      return preTxt;
    };

    const fields = {
      workflow_1: [
        [
          {
            type: "label",
            label: label1_1,
          },
          {
            type: "input",
            inputProp: {
              value: jsonData.different_workflow_1_text,
              onChange: (evt) => {
                const previewPre = generateWorkflow1Pre(
                  evt.target.value,
                  jsonData.different_workflow_1_select
                );
                handleJsonDataChange(
                  "different_workflow_1_text",
                  evt.target.value,
                  previewPre,
                  `${previewPre} ${jsonData?.add_free_text || ""}`
                );
              },
              style: { width: "75%" },
            },
          },
        ],
        [
          {
            type: "label",
            label: label1_2,
          },
          {
            type: "select",
            inputProp: {
              value: jsonData.different_workflow_1_select,
              onChange: (val) => {
                const previewPre = generateWorkflow1Pre(
                  jsonData.different_workflow_1_text,
                  val
                );
                handleJsonDataChange(
                  "different_workflow_1_select",
                  val,
                  previewPre,
                  `${previewPre} ${jsonData?.add_free_text || ""}`
                );
              },
              options: workflow1Options,
              style: { width: 60 },
            },
          },
          {
            type: "label",
            label: label1_3,
          },
        ],
      ],
      workflow_2: [
        [
          {
            type: "label",
            label: label2_1,
          },
          {
            type: "input",
            inputProp: {
              value: jsonData.different_workflow_2_text,
              onChange: (evt) => {
                const previewPre = generateWorkflow2Pre(
                  evt.target.value,
                  jsonData.different_workflow_2_select
                );
                handleJsonDataChange(
                  "different_workflow_2_text",
                  evt.target.value,
                  previewPre,
                  `${previewPre} ${jsonData?.add_free_text || ""}`
                );
              },
              style: { width: "75%" },
            },
          },
        ],
        [
          {
            type: "select",
            inputProp: {
              value: jsonData.different_workflow_2_select,
              onChange: (val) => {
                const previewPre = generateWorkflow2Pre(
                  jsonData.different_workflow_2_text,
                  val
                );
                handleJsonDataChange(
                  "different_workflow_2_select",
                  val,
                  previewPre,
                  `${previewPre} ${jsonData?.add_free_text || ""}`
                );
              },
              options: workflow2Options,
              style: { width: 100 },
            },
          },
          {
            type: "label",
            label: label2_2,
          },
        ],
      ],
    };

    return (
      <>
        <label className={styles.commonLabel}>
          What is the new workflow for the patient?{" "}
          <span className="input-required">*</span>
        </label>
        <div className={styles.pageInput}>
          <Select
            className="common-style-select"
            value={jsonData.differentWorkflowType}
            onChange={(val) => {
              let previewText =
                val === "workflow_1"
                  ? generateWorkflow1Pre("", null)
                  : val === "workflow_2"
                  ? generateWorkflow2Pre("", null)
                  : "";
              handleJsonDataChange(
                "differentWorkflowType",
                val,
                "",
                previewText
              );
              setWorkflowErrorMsg("");
              setErrorMsg("");
            }}
            options={workflowTypeOptions}
            allowClear
            style={{ width: "100%" }}
          />
          {workflowErrorMsg && (
            <div className={styles.errorMsg}>{workflowErrorMsg}</div>
          )}
        </div>

        {jsonData.differentWorkflowType ? (
          <>
            {fields[jsonData.differentWorkflowType].map((group, i) => (
              <Input.Group className={styles.inputGroup} key={i}>
                {group.map((field, index) => (
                  <FillInBlankInputs {...field} key={index} />
                ))}
              </Input.Group>
            ))}
            {errorMsg && <div className={styles.errorMsg}>{errorMsg}</div>}
          </>
        ) : (
          ""
        )}
      </>
    );
  }
);

/*
  Component: Details for "Activity repeated in certain cases"
*/
export const RepeatInCertainCase = forwardRef(
  (
    { name, jsonData, configOptions, handleJsonDataChange }: CommonDetailProps,
    ref: any
  ) => {
    const IS_VERB = 1;
    const IS_TIMES = 2;

    useImperativeHandle(ref, () => ({
      doValidation: () => doValidation(),
    }));

    const [errorMsg, setErrorMsg] = useState<string>("");

    const doValidation = () => {
      let isValid = true;
      if (
        !jsonData?.repeat_in_centain_case_verb_select ||
        !jsonData?.repeat_in_centain_case_times_select ||
        !jsonData?.repeat_in_centain_case_if_text?.trim()
      ) {
        setErrorMsg("Please fill in the sentence.");
        isValid = false;
      } else {
        setErrorMsg("");
      }
      return isValid;
    };

    const verbOptions = configOptions
      .filter((item) => item.dropdown_id === IS_VERB)
      .map((item) => ({
        value: item.fd_dropdown_id,
        label: item.dropdown_text,
      }));

    const timesOptons = configOptions
      .filter((item) => item.dropdown_id === IS_TIMES)
      .map((item) => ({
        value: item.fd_dropdown_id,
        label: item.dropdown_text,
      }));

    const label1 = name;
    const label2 = `be repeated`;
    const label3 = `time(s) if`;

    const generatePre = (
      verb_select: number,
      times_select: number,
      case_if_text: string
    ) => {
      if (
        !Boolean(verb_select) &&
        !Boolean(times_select) &&
        !Boolean(case_if_text)
      ) {
        return "";
      }

      const verbSelectLabel = verb_select
        ? verbOptions.find((item) => item.value === verb_select).label
        : "";
      const timesSelectLabel = times_select
        ? timesOptons.find((item) => item.value === times_select).label
        : "";
      const preTxt = `${label1} ${verbSelectLabel} ${label2} ${timesSelectLabel} ${label3} ${
        case_if_text?.trim() || ""
      }`;
      return preTxt;
    };

    const fields = [
      [
        {
          type: "text",
          label: label1,
        },
        {
          type: "select",
          inputProp: {
            value: jsonData.repeat_in_centain_case_verb_select,
            onChange: (val) => {
              const previewPre = generatePre(
                val,
                jsonData.repeat_in_centain_case_times_select,
                jsonData.repeat_in_centain_case_if_text
              );
              handleJsonDataChange(
                "repeat_in_centain_case_verb_select",
                val,
                previewPre,
                `${previewPre} ${jsonData?.add_free_text || ""}`
              );
            },
            options: verbOptions,
            allowClear: true,
            style: { width: 110 },
          },
        },
        {
          type: "text",
          label: label2,
        },
        {
          type: "select",
          inputProp: {
            value: jsonData.repeat_in_centain_case_times_select,
            onChange: (val) => {
              const previewPre = generatePre(
                jsonData.repeat_in_centain_case_verb_select,
                val,
                jsonData.repeat_in_centain_case_if_text
              );
              handleJsonDataChange(
                "repeat_in_centain_case_times_select",
                val,
                previewPre,
                `${previewPre} ${jsonData?.add_free_text || ""}`
              );
            },
            options: timesOptons,
            allowClear: true,
            style: { width: 125 },
          },
        },
        {
          type: "text",
          label: label3,
        },
        {
          type: "input",
          inputProp: {
            value: jsonData.repeat_in_centain_case_if_text,
            onChange: (evt) => {
              const previewPre = generatePre(
                jsonData.repeat_in_centain_case_verb_select,
                jsonData.repeat_in_centain_case_times_select,
                evt.target.value
              );
              handleJsonDataChange(
                "repeat_in_centain_case_if_text",
                evt.target.value,
                previewPre,
                `${previewPre} ${jsonData?.add_free_text || ""}`
              );
            },
            style: { width: "65%" },
          },
        },
      ],
    ];

    return (
      <>
        {fields.map((group, i) => (
          <Input.Group className={styles.inputGroup} key={i}>
            {group.map((field, index) => (
              <FillInBlankInputs {...field} key={index} />
            ))}
          </Input.Group>
        ))}
        {errorMsg && <div className={styles.errorMsg}>{errorMsg}</div>}
      </>
    );
  }
);

/*
  Component: Details for "Activity is continuously monitored"
*/
export const ContinuouslyMonitored = forwardRef(
  (
    { name, jsonData, configOptions, handleJsonDataChange }: CommonDetailProps,
    ref: any
  ) => {
    const location = useLocation();
    const locationState = location.state as SoaDetailsState;
    const dpSoaState = useSelector((state: any) => state?.dpSoaReducer);
    const maxVisit = dpSoaState.soaVisits[dpSoaState.soaVisits.length - 1];

    const IS_FROM = 1;
    // const IS_TO = 2;

    useImperativeHandle(ref, () => ({
      doValidation: () => doValidation(),
    }));

    const [errorMsg, setErrorMsg] = useState<string>("");

    const doValidation = () => {
      let isValid = true;
      const fromVisitIdItem = fromOptions.find(
        (item) => item.label === "Visit"
      );
      const fromWeekId = fromOptions.find(
        (item) => item.label === "Week"
      ).value;
      const fromDayId = fromOptions.find((item) => item.label === "Day").value;
      if (
        !jsonData?.continuously_monitored_from_select ||
        typeof jsonData?.continuously_monitored_from_number_text !== "number" ||
        !jsonData?.continuously_monitored_to_select ||
        typeof jsonData?.continuously_monitored_to_number_text !== "number"
      ) {
        setErrorMsg("Please fill in the sentence.");
        isValid = false;
      } else {
        // for Day, number should be between -7000 and 7000
        // for Week, number should be between -1000 and 1000
        // for Visit, number should be greater than 0
        if (
          !Number.isInteger(
            jsonData?.continuously_monitored_from_number_text
          ) ||
          !Number.isInteger(jsonData?.continuously_monitored_to_number_text)
        ) {
          setErrorMsg("Invalid input. Decimal values are not allowed.");
          isValid = false;
        } else if (
          fromWeekId === jsonData?.continuously_monitored_from_select &&
          (jsonData?.continuously_monitored_from_number_text < -1000 ||
            jsonData?.continuously_monitored_from_number_text > 1000 ||
            jsonData?.continuously_monitored_to_number_text < -1000 ||
            jsonData?.continuously_monitored_to_number_text > 1000)
        ) {
          setErrorMsg("The value for Weeks must be between -1000 and 1000.");
          isValid = false;
        } else if (
          fromDayId === jsonData?.continuously_monitored_from_select &&
          (jsonData?.continuously_monitored_from_number_text < -7000 ||
            jsonData?.continuously_monitored_from_number_text > 7000 ||
            jsonData?.continuously_monitored_to_number_text < -7000 ||
            jsonData?.continuously_monitored_to_number_text > 7000)
        ) {
          setErrorMsg("The value for Days must be between -7000 and 7000.");
          isValid = false;
        } else if (
          ![fromWeekId, fromDayId].includes(
            jsonData?.continuously_monitored_from_select
          ) &&
          (jsonData?.continuously_monitored_from_number_text <= 0 ||
            jsonData?.continuously_monitored_to_number_text <= 0 ||
            !Number.isInteger(
              jsonData?.continuously_monitored_from_number_text
            ) ||
            !Number.isInteger(jsonData?.continuously_monitored_to_number_text))
        ) {
          setErrorMsg("The value for Visits must be greater than 0.");
          isValid = false;
        } else if (
          jsonData?.continuously_monitored_from_number_text >
          jsonData?.continuously_monitored_to_number_text
        ) {
          setErrorMsg(
            "The value should to be greater or equal to the from value."
          );
          isValid = false;
        } else if (
          (jsonData?.continuously_monitored_from_select ===
            fromVisitIdItem.value &&
            jsonData?.continuously_monitored_from_number_text > maxVisit) ||
          (jsonData?.continuously_monitored_to_select ===
            fromVisitIdItem.value &&
            jsonData?.continuously_monitored_to_number_text > maxVisit)
        ) {
          setErrorMsg(
            "The No. of visits should be limited to maximum No. of total visits in the SoA builder table."
          );
          isValid = false;
        } else {
          setErrorMsg("");
        }
      }
      return isValid;
    };

    const fromOptions = configOptions
      .filter((item) => item.dropdown_id === IS_FROM)
      .map((item) => ({
        value: item.fd_dropdown_id,
        label: item.dropdown_text,
        disabled:
          item.dropdown_text.toLowerCase() !==
            locationState?.unit?.toLowerCase() &&
          item.dropdown_text.toLowerCase() !== "visit",
      }));

    const label1 = `${name} is an ongoing activity that occurs from`;
    const label2 = `to`;

    const generatePre = (
      from_select: number,
      from_number_text: string,
      to_select: number,
      to_number_text: string
    ) => {
      if (
        !Boolean(from_select) &&
        typeof from_number_text !== "number" &&
        !Boolean(to_select) &&
        typeof to_number_text !== "number"
      ) {
        return "";
      }

      const fromSelectLabel = from_select
        ? fromOptions.find((item) => item.value === from_select).label
        : "";
      const toSelectLabel = to_select
        ? fromOptions.find((item) => item.value === to_select).label
        : "";
      const preTxt = `${label1} ${fromSelectLabel} ${
        typeof from_number_text === "number" ? from_number_text : ""
      } ${label2} ${toSelectLabel} ${
        typeof to_number_text === "number" ? to_number_text : ""
      }.`;
      return preTxt;
    };

    const fields = [
      [
        {
          type: "text",
          label: label1,
        },
        {
          type: "select",
          inputProp: {
            value: jsonData.continuously_monitored_from_select,
            onChange: (val) => {
              // when the user selects the Weeks at from dropdown box the to dropdown box should also auto select with weeks,likewise for days and visits.
              const fromItem = fromOptions.find((from) => from.value === val);
              if (fromItem) {
                const previewPre = generatePre(
                  val,
                  jsonData.continuously_monitored_from_number_text,
                  val,
                  jsonData.continuously_monitored_to_number_text
                );
                const option_id_for_from_day = fromOptions.find(
                  (from) => from.label === "Day"
                );
                const option_id_for_from_week = fromOptions.find(
                  (from) => from.label === "Week"
                );
                handleJsonDataChange(
                  [
                    "continuously_monitored_from_select",
                    "continuously_monitored_to_select",
                    "day_week_visit_type",
                    "continuously_monitored_the_option_id_for_from_day",
                    "continuously_monitored_the_option_id_for_from_week",
                    "continuously_monitored_the_option_id_for_to_day",
                    "continuously_monitored_the_option_id_for_to_week",
                  ],
                  [
                    val,
                    val,
                    fromItem.label,
                    option_id_for_from_day.value,
                    option_id_for_from_week.value,
                    option_id_for_from_day.value,
                    option_id_for_from_week.value,
                  ],
                  previewPre,
                  `${previewPre} ${jsonData?.add_free_text || ""}`
                );
              } else {
                const previewPre = generatePre(
                  val,
                  jsonData.continuously_monitored_from_number_text,
                  val,
                  jsonData.continuously_monitored_to_number_text
                );
                handleJsonDataChange(
                  [
                    "continuously_monitored_from_select",
                    "continuously_monitored_to_select",
                    "day_week_visit_type",
                  ],
                  [val, val, undefined],
                  previewPre,
                  `${previewPre} ${jsonData?.add_free_text || ""}`
                );
              }
            },
            options: fromOptions,
            allowClear: true,
            style: { width: 100 },
          },
        },
        {
          type: "number",
          inputProp: {
            value: jsonData.continuously_monitored_from_number_text,
            onChange: (val) => {
              const previewPre = generatePre(
                jsonData.continuously_monitored_from_select,
                val,
                jsonData.continuously_monitored_to_select,
                jsonData.continuously_monitored_to_number_text
              );
              handleJsonDataChange(
                "continuously_monitored_from_number_text",
                val,
                previewPre,
                `${previewPre} ${jsonData?.add_free_text || ""}`
              );
            },
            maxLength: 5,
            style: { width: 60 },
          },
        },
        {
          type: "text",
          label: label2,
        },
        {
          type: "select",
          inputProp: {
            value: jsonData.continuously_monitored_to_select,
            onChange: (val) => {
              const previewPre = generatePre(
                jsonData.continuously_monitored_from_select,
                jsonData.continuously_monitored_from_number_text,
                val,
                jsonData.continuously_monitored_to_number_text
              );
              handleJsonDataChange(
                "continuously_monitored_to_select",
                val,
                previewPre,
                `${previewPre} ${jsonData?.add_free_text || ""}`
              );
            },
            options: fromOptions,
            style: { width: 100 },
            disabled: true,
            showArrow: false,
          },
        },
        {
          type: "number",
          inputProp: {
            value: jsonData.continuously_monitored_to_number_text,
            onChange: (val) => {
              const previewPre = generatePre(
                jsonData.continuously_monitored_from_select,
                jsonData.continuously_monitored_from_number_text,
                jsonData.continuously_monitored_to_select,
                val
              );
              handleJsonDataChange(
                "continuously_monitored_to_number_text",
                val,
                previewPre,
                `${previewPre} ${jsonData?.add_free_text || ""}`
              );
            },
            maxLength: 5,
            style: { width: 60 },
          },
        },
      ],
    ];

    return (
      <>
        {fields.map((group, i) => (
          <Input.Group className={styles.inputGroup} key={i}>
            {group.map((field, index) => (
              <FillInBlankInputs {...field} key={index} />
            ))}
          </Input.Group>
        ))}
        {errorMsg && <div className={styles.errorMsg}>{errorMsg}</div>}
      </>
    );
  }
);

/*
  Component: Details for "Activity uses multiple modalities"
*/
export const MultipleModalities = forwardRef(
  (
    {
      name,
      activity,
      jsonData,
      configOptions,
      handleJsonDataChange,
    }: CommonDetailProps,
    ref: any
  ) => {
    const IS_CHECKBOX = 1;

    useImperativeHandle(ref, () => ({
      doValidation: () => doValidation(),
    }));

    const [errorMsg, setErrorMsg] = useState<string>("");

    const doValidation = () => {
      let isValid = true;
      if (!(jsonData?.multiple_modalities_allow_list_select?.length > 0)) {
        setErrorMsg("Modalities is required.");
        isValid = false;
      } else {
        setErrorMsg("");
      }
      return isValid;
    };

    const options = configOptions
      .filter((item) => item.dropdown_id === IS_CHECKBOX)
      .map((item, index) => ({
        value: item.fd_dropdown_id,
        label: item.dropdown_text,
        disabled: activity ? !Boolean(activity?.modality[index]) : false,
      }));

    const onChange = (checkedValues: CheckboxValueType[]) => {
      const includesSelectedList = options.filter((item) =>
        checkedValues.includes(item.value)
      );
      const includesText = includesSelectedList
        .map((item, index) => {
          if (index !== 0 && index === includesSelectedList.length - 1) {
            return `and ${item.label}.`;
          } else {
            return `${item.label}${
              includesSelectedList.length === 1 ? "." : ""
            }`;
          }
        })
        .join(", ");
      const previewPre =
        checkedValues.length > 0
          ? `${name} can be performed through ${includesText}`
          : "";
      handleJsonDataChange(
        "multiple_modalities_allow_list_select",
        checkedValues,
        previewPre,
        previewPre
      );
      if (checkedValues.length > 0) {
        setErrorMsg("");
      }
    };

    return (
      <>
        <label className={styles.commonLabel}>
          {`Which modalities are allowed to perform ${name}?`}{" "}
          <span className="input-required">*</span>
        </label>
        <div className={styles.checkboxWrapper}>
          <Checkbox.Group
            value={jsonData.multiple_modalities_allow_list_select}
            onChange={onChange}
          >
            {options.map((item) => (
              <div className={styles.checkboxItem} key={item.value}>
                <Checkbox value={item.value} disabled={item.disabled}>
                  {item.label}
                </Checkbox>
              </div>
            ))}
          </Checkbox.Group>
          {errorMsg && <div className={styles.errorMsg}>{errorMsg}</div>}
        </div>
      </>
    );
  }
);

/*
  Component: Details for "Free text footnote"
*/
export const FreeText = forwardRef(
  ({ jsonData, handleJsonDataChange }: CommonDetailProps, ref: any) => {
    useImperativeHandle(ref, () => ({
      doValidation: () => doValidation(),
    }));

    const [errorMsg, setErrorMsg] = useState<string>("");

    const doValidation = () => {
      let isValid = true;
      if (!jsonData?.free_text_footnote_text?.trim()) {
        setErrorMsg("Describe the footnote is required.");
        isValid = false;
      } else {
        setErrorMsg("");
      }
      return isValid;
    };

    return (
      <>
        <label className={styles.commonLabel}>
          Describe the footnote <span className="input-required">*</span>
        </label>
        <TextArea
          value={jsonData.free_text_footnote_text}
          onChange={(e) =>
            handleJsonDataChange(
              "free_text_footnote_text",
              e.target.value,
              e.target.value?.trim(),
              e.target.value?.trim()
            )
          }
          autoSize={{ minRows: 2, maxRows: 2 }}
          showCount
        />
        {errorMsg && <div className={styles.errorMsg}>{errorMsg}</div>}
      </>
    );
  }
);
